import { useEffect, useState, type ReactNode } from 'react';
import type { PropertyDefinition } from '@peruselab/api-client/assertions';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import {
  canShowIn,
  conditionTexts,
  formatDifference,
  formatIn,
  formatValue
} from '../chemistry/molecule/assertionText';
import type { MoleculePropertiesState } from '../chemistry/molecule/useMoleculeProperties';
import { temperatureUnitPreference, TEMPERATURE_UNITS } from '../../preferences/displayPreferences';
import { usePreference } from '../../preferences/storedPreference';
import { propertyCards, type PropertyCard } from './propertyCards';

export interface EvidenceValuesRequest {
  readonly propertyId: string;
  /** A value was added: show its property. */
  readonly onAdded: (propertyId: string) => void;
  /** The reader's temperature unit for a temperature property, or null to show each as listed. */
  readonly displayUnit: string | null;
}

interface PropertyEvidenceProps {
  readonly name: string | null;
  readonly properties: MoleculePropertiesState;
  /** The property to open on (a card was tapped, or a comment's anchor names one of its values). */
  readonly focusPropertyId: string | null;
  /** Every value of the chosen property (PropertiesSection, layout "evidence"). */
  readonly renderValues: (request: EvidenceValuesRequest) => ReactNode;
}

function AgreementBadge({ card, unit }: { readonly card: PropertyCard; readonly unit: string | null }) {
  const { t, i18n } = useTranslation();
  if (!card.preferred) return <span className="evidence-badge">{t('properties.report.noPreferred')}</span>;
  if (card.agreement === 'single') {
    return <span className="evidence-badge">{t('properties.evidence.single')}</span>;
  }
  if (card.agreement === 'agree') {
    return (
      <span className="evidence-badge evidence-badge--agree">
        <Icon name="star" size="0.9rem" />
        {t('properties.evidence.agree', { count: card.count })}
      </span>
    );
  }
  const amount = formatDifference(
    card.spread,
    card.preferred.siUnit,
    unit ?? card.preferred.value.unit,
    card.property,
    i18n.language
  );
  return <span className="evidence-badge evidence-badge--differ">{t('properties.evidence.differ', { amount })}</span>;
}

/**
 * #931 (epic #934): every value reported for one property, where each came from, whether they agree
 * (within the catalog's tolerance), and which one is preferred and why -- by the stated rule only
 * (ADR 0018 D9; owner, 2026-10-11: no "Make preferred"). Temperatures can be read in °C, °F or K.
 */
export function PropertyEvidence({ name, properties, focusPropertyId, renderValues }: PropertyEvidenceProps) {
  const { t, i18n } = useTranslation();
  const temperatureUnit = usePreference(temperatureUnitPreference);
  const cards = properties.loaded ? propertyCards(properties.loaded) : [];
  const [selected, setSelected] = useState<string | null>(focusPropertyId);
  useEffect(() => {
    if (focusPropertyId) setSelected(focusPropertyId);
  }, [focusPropertyId]);
  // Stay on the property shown even when its last value is withdrawn, so its history stays in reach.
  const firstId = cards[0]?.property.id ?? null;
  useEffect(() => {
    if (!selected && firstId) setSelected(firstId);
  }, [selected, firstId]);

  if (!properties.loaded) return null;
  const shownId = selected ?? firstId ?? properties.loaded.catalog[0]?.id ?? '';
  const card = cards.find((candidate) => candidate.property.id === shownId);
  const list =
    cards.length > 0 ? (
      <nav className="evidence-list" aria-label={t('properties.evidence.listLabel')}>
        <p>{t('properties.evidence.listHeading', { name: name ?? '', count: cards.length })}</p>
        <ul>
          {cards.map((candidate) => (
            <li key={candidate.property.id}>
              <button
                type="button"
                aria-current={candidate.property.id === shownId ? 'true' : undefined}
                className={candidate.property.id === shownId ? 'active' : ''}
                onClick={() => setSelected(candidate.property.id)}
              >
                <span className="evidence-list-name">{candidate.property.name}</span>
                <span className="evidence-list-value" dir="ltr">
                  {candidate.preferred
                    ? ((canShowIn(candidate.preferred.siUnit, temperatureUnit)
                        ? formatIn(candidate.preferred, temperatureUnit, candidate.property, i18n.language)
                        : null) ?? formatValue(candidate.preferred, candidate.property))
                    : t('properties.report.noPreferred')}
                </span>
                {candidate.sourced > 0 ? (
                  <span className="monograph-chip">{t('monograph.sourced', { count: candidate.sourced })}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    ) : null;
  // One tree whether or not the property has a current value, so the values (and "Show history")
  // stay mounted when its last value is withdrawn.
  const shownProperty = properties.loaded.catalog.find((property) => property.id === shownId);
  const temperature = card?.preferred ? canShowIn(card.preferred.siUnit, temperatureUnit) : false;
  const unit = temperature ? temperatureUnit : null;
  const summary = card ? <EvidenceSummary card={card} unit={unit} catalog={properties.loaded.catalog} /> : null;

  return (
    <section className="property-evidence" aria-label={t('properties.heading')}>
      {list}
      <div className="evidence-main">
        {summary}
        {renderValues({ propertyId: shownProperty?.id ?? shownId, displayUnit: unit, onAdded: setSelected })}
      </div>
    </section>
  );
}

function EvidenceSummary({
  card,
  unit,
  catalog
}: {
  readonly card: PropertyCard;
  readonly unit: string | null;
  readonly catalog: readonly PropertyDefinition[];
}) {
  const { t, i18n } = useTranslation();
  const temperatureUnit = usePreference(temperatureUnitPreference);
  if (!card.preferred)
    return (
      <section
        className="evidence-summary"
        aria-label={t('properties.evidence.summaryLabel', { property: card.property.name })}
      >
        <h2>{card.property.name}</h2>
        <p>{t('properties.report.noPreferred')}</p>
      </section>
    );
  const shown =
    (unit ? formatIn(card.preferred, unit, card.property, i18n.language) : null) ??
    formatValue(card.preferred, card.property);
  const { quantities, solvent } = conditionTexts(card.preferred, catalog);
  const listed = formatValue(card.preferred, card.property);
  const reason = card.summary && card.count > 1 ? card.summary.reasons[0] : null;
  const preferredLine = [
    card.source,
    shown !== listed ? t('properties.listedAs', { value: listed }) : null,
    ...quantities.map((text) => t('properties.at.quantity', { value: text })),
    solvent ? `${t('properties.in')} ${solvent}` : null
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section
      className="evidence-summary"
      aria-label={t('properties.evidence.summaryLabel', { property: card.property.name })}
    >
      <div className="evidence-summary-text">
        <h2>{card.property.name}</h2>
        <p className="evidence-summary-value" dir="ltr">
          {shown}
        </p>
        <p className="evidence-summary-line">
          <Icon name="star" size="0.95rem" />
          <span>
            {t('properties.preferred')}
            {preferredLine ? ` · ${preferredLine}` : ''}
          </span>
        </p>
        {reason ? <p className="evidence-summary-why">{t(`properties.preferredWhy.${reason}`)}</p> : null}
      </div>
      <div className="evidence-summary-side">
        <AgreementBadge card={card} unit={unit} />
        {unit ? (
          <div className="evidence-unit-switch" role="group" aria-label={t('properties.evidence.unitLabel')}>
            {TEMPERATURE_UNITS.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={temperatureUnit === code}
                className={temperatureUnit === code ? 'active' : ''}
                onClick={() => temperatureUnitPreference.set(code)}
              >
                {card.property.units.find((option) => option.code === code)?.symbol ?? code}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
